import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';

import { PromoProductTypes } from '~/services/PromoService';

type ProductTypeEligibilityHelperProps = {
  promoProductTypes: PromoProductTypes;
  context: 'discount' | 'minSpend';
  selectedProductValues?: Array<string>;
};

type ProductTypeEligibilityRow = {
  applies: boolean;
  name: string;
};

type SelectedProductTypeEligibilityRow = ProductTypeEligibilityRow & {
  isCoveredBySelection: boolean;
};

const HELPER_COPY_BY_CONTEXT: Record<ProductTypeEligibilityHelperProps['context'], string> = {
  discount: 'The products this discount applies to.',
  minSpend: 'Only these product types count when checking whether the customer meets the minimum spend.',
};

function getProductOptionName(
  productType: PromoProductTypes[number],
  product: PromoProductTypes[number]['products'][number],
) {
  return product.productAdminName || productType.categoryName || `${productType.category_bk}-${product.product_bk}`;
}

function getDisplayableProductOptions(productType: PromoProductTypes[number]) {
  const productSubTypes = productType.products.filter(
    (product) => product.product_bk !== 'ALL' && !product.dontShowInAdmin,
  );

  if (productSubTypes.length > 0) {
    return productSubTypes;
  }

  return productType.products.filter((product) => !product.dontShowInAdmin);
}

function getProductOptionApplicability(
  productType: PromoProductTypes[number],
  product: PromoProductTypes[number]['products'][number],
  context: ProductTypeEligibilityHelperProps['context'],
  isSelectedByGroupOption: boolean,
) {
  if (context === 'discount') {
    return !isSelectedByGroupOption || !productType.excludeFromALLDiscounting;
  }

  return productType.canUseForProductLevelMinSpend && product.canUseForProductLevelMinSpend;
}

function getSelectedProductOptionDisplayRows(
  promoProductTypes: PromoProductTypes,
  context: ProductTypeEligibilityHelperProps['context'],
  selectedProductValues: Array<string>,
): Array<SelectedProductTypeEligibilityRow> {
  const selectedProductValueSet = new Set(selectedProductValues);

  return promoProductTypes
    .flatMap((productType) => {
      const isSelectedByGroupOption =
        selectedProductValueSet.has('ALL-ALL') || selectedProductValueSet.has(`${productType.category_bk}-ALL`);

      return getDisplayableProductOptions(productType).map((product) => {
        const isCoveredBySelection =
          isSelectedByGroupOption || selectedProductValueSet.has(`${productType.category_bk}-${product.product_bk}`);

        return {
          applies:
            isCoveredBySelection &&
            getProductOptionApplicability(productType, product, context, isSelectedByGroupOption),
          isCoveredBySelection,
          name: getProductOptionName(productType, product),
        };
      });
    })
    .sort((firstProductType, secondProductType) => firstProductType.name.localeCompare(secondProductType.name));
}

function getSelectedProductOptionApplicabilityRows(
  promoProductTypes: PromoProductTypes,
  context: ProductTypeEligibilityHelperProps['context'],
  selectedProductValues: Array<string>,
): Array<ProductTypeEligibilityRow> {
  return getSelectedProductOptionDisplayRows(promoProductTypes, context, selectedProductValues).map(
    ({ applies, name }) => ({
      applies,
      name,
    }),
  );
}

export function getProductTypeEligibilityRows(
  promoProductTypes: PromoProductTypes,
  context: ProductTypeEligibilityHelperProps['context'],
  selectedProductValues?: Array<string>,
) {
  if (selectedProductValues) {
    return getSelectedProductOptionApplicabilityRows(promoProductTypes, context, selectedProductValues);
  }

  return promoProductTypes
    .flatMap((productType) =>
      getDisplayableProductOptions(productType).map((product) => ({
        applies: getProductOptionApplicability(productType, product, context, true),
        name: getProductOptionName(productType, product),
      })),
    )
    .sort((firstProductType, secondProductType) => firstProductType.name.localeCompare(secondProductType.name));
}

export function getProductTypeEligibilityHelperTitle(
  promoProductTypes: PromoProductTypes,
  context: ProductTypeEligibilityHelperProps['context'],
  selectedProductValues?: Array<string>,
) {
  const excludedProductTypeNames = selectedProductValues
    ? getSelectedProductOptionDisplayRows(promoProductTypes, context, selectedProductValues)
        .filter((productType) => productType.isCoveredBySelection && !productType.applies)
        .map((productType) => productType.name)
    : getProductTypeEligibilityRows(promoProductTypes, context)
        .filter((productType) => !productType.applies)
        .map((productType) => productType.name);
  const baseCopy = HELPER_COPY_BY_CONTEXT[context];

  if (excludedProductTypeNames.length === 0) {
    return baseCopy;
  }

  if (selectedProductValues) {
    return `${baseCopy} ${excludedProductTypeNames.length} selected product option${
      excludedProductTypeNames.length === 1 ? '' : 's'
    } will not count: ${excludedProductTypeNames.join(', ')}. Add the specific eligible options explicitly if you want items of this type to be eligible.`;
  }

  return `${baseCopy} "All Products" still excludes ${excludedProductTypeNames.length} product option${
    excludedProductTypeNames.length === 1 ? '' : 's'
  }: ${excludedProductTypeNames.join(', ')}. Add these explicitly if you want items of this type to be eligible.`;
}

export default function ProductTypeEligibilityHelper({
  promoProductTypes,
  context,
  selectedProductValues,
}: ProductTypeEligibilityHelperProps) {
  const productTypeApplicabilityRows = getProductTypeEligibilityRows(promoProductTypes, context, selectedProductValues);
  const applicableCount = productTypeApplicabilityRows.filter((productType) => productType.applies).length;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {HELPER_COPY_BY_CONTEXT[context]}
      </Typography>
      {productTypeApplicabilityRows.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ bgcolor: 'transparent', mt: 0.5, '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize="small" />}
            sx={{ minHeight: 28, px: 0, '& .MuiAccordionSummary-content': { my: 0 } }}
          >
            <Typography variant="caption" color="text.secondary">
              Product type eligibility ({applicableCount}/{productTypeApplicabilityRows.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, py: 0 }}>
            <Stack spacing={0.5}>
              {productTypeApplicabilityRows.map((productType) => (
                <Stack key={productType.name} direction="row" alignItems="center" spacing={0.75}>
                  {productType.applies ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <HighlightOffIcon color="disabled" fontSize="small" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {productType.name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
